export type PassFieldValueSource =
  | "static"
  | "event_name"
  | "guest_name"
  | "guest_email"
  | "ticket_id"
  | "form_name"
  | "event_venue"
  | "hide"

// Every main field offers the same choice: leave it to the event's own data, or
// override it. Only the wording of the default differs.
export type PassFieldMode = "default" | "override"

const modeOptions = (defaultLabel: string): { label: string; value: PassFieldMode }[] => [
  { label: defaultLabel, value: "default" },
  { label: "Custom", value: "override" },
]

export const PASS_BRAND_NAME_MODE_OPTIONS = modeOptions("Use Account Name")
export const PASS_HEADER_MODE_OPTIONS = modeOptions("Use Event Date and Time")
export const PASS_EVENT_TITLE_MODE_OPTIONS = modeOptions("Use Public Event Title")
export const PASS_EVENT_LOCATION_MODE_OPTIONS = modeOptions("Use Event Venue")

export type PassBackSectionType = "email" | "phone" | "website" | "location" | "text"

export type PassBackSection = {
  id: string
  type: PassBackSectionType | ""
  label: string
  value: string
}

export const PASS_BACK_SECTION_TYPE_LABELS: Record<PassBackSectionType, string> = {
  email: "Email",
  phone: "Phone",
  website: "Website",
  location: "Location",
  text: "Text",
}

export const PASS_BACK_SECTION_TYPE_ICONS: Record<PassBackSectionType, string> = {
  email: "email-only-large",
  phone: "contacts-phone",
  website: "website",
  location: "event-where",
  text: "contacts-text",
}

/** Sketch Pass Builder: type dropdown menu order */
export const PASS_BACK_SECTION_TYPES: PassBackSectionType[] = [
  "text",
  "email",
  "phone",
  "website",
  "location",
]

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/** Prepends https:// to schemeless website values — same rule as the API. */
export const normalizeBackSectionValue = (
  type: PassBackSectionType | "",
  value: string,
): string => {
  const trimmed = value.trim()
  if (type === "website" && trimmed && !/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`
  }

  return trimmed
}

/** Mirrors the API's link-building rules so organizers see problems before
 *  saving; values that fail these checks render as plain text on the Google
 *  pass instead of tappable links. Empty values are allowed. */
export const validateBackSectionValue = (
  type: PassBackSectionType | "",
  value: string,
): string | undefined => {
  const trimmed = value.trim()
  if (!trimmed) return undefined

  switch (type) {
    case "email":
      return EMAIL_REGEX.test(trimmed) ? undefined : "Enter a valid email address"
    case "phone":
      return (trimmed.replace(/[^\d]/g, "").length >= 3 && /^[\d\s+\-().*#]+$/.test(trimmed))
        ? undefined
        : "Enter a valid phone number"
    case "website": {
      try {
        const url = new URL(normalizeBackSectionValue(type, trimmed))

        return url.hostname.includes(".") ? undefined : "Enter a valid URL"
      } catch {
        return "Enter a valid URL"
      }
    }
    default:
      return undefined
  }
}

export type WalletPassBuilderFormValues = {
  squareLogo: File | null
  squareLogoUrl: string
  horizontalLogo: File | null
  horizontalLogoUrl: string
  appleBanner: File | null
  appleBannerUrl: string
  googleHero: File | null
  googleHeroUrl: string
  backgroundColor: string
  textColor: string
  labelColor: string
  brandNameMode: PassFieldMode
  logoText: string
  // UI only: "default" leaves a field to the event's own data, which is what an
  // empty value means to the pass. Derived from the stored values rather than
  // persisted, so the control and the pass can never disagree.
  headerMode: PassFieldMode
  headerLabel: string
  headerValueSource: PassFieldValueSource
  headerStaticValue: string
  eventTitleMode: PassFieldMode
  primaryLabel: string
  primaryValueSource: PassFieldValueSource
  primaryStaticValue: string
  // When an Apple banner is set, mirror the Primary field into Aux 1 and drop
  // it from the Apple front (the banner would otherwise sit under it). Google
  // keeps Primary as the holder name.
  primaryMovedBelowBanner: boolean
  eventLocationMode: PassFieldMode
  secondaryLabel: string
  secondaryValueSource: PassFieldValueSource
  secondaryStaticValue: string
  auxiliary1Enabled: boolean
  auxiliary1Label: string
  auxiliary1ValueSource: PassFieldValueSource
  auxiliary1StaticValue: string
  auxiliary2Enabled: boolean
  auxiliary2Label: string
  auxiliary2ValueSource: PassFieldValueSource
  auxiliary2StaticValue: string
  // Guest Name has its own row above the QR code, so it no longer eats an
  // auxiliary slot. Its value is fixed; only the Apple label is editable.
  guestNameEnabled: boolean
  guestNameLabel: string
  qrCodeEnabled: boolean
  backSections: PassBackSection[]
}

export const PASS_FIELD_VALUE_OPTIONS: { label: string; value: PassFieldValueSource }[] = [
  { label: "Static Text", value: "static" },
  { label: "Public Event Title", value: "event_name" },
  { label: "Guest Name", value: "guest_name" },
  { label: "Guest Email", value: "guest_email" },
  { label: "Ticket ID", value: "ticket_id" },
  { label: "Form Name", value: "form_name" },
  { label: "Event Venue", value: "event_venue" },
]

export const PASS_AUXILIARY_FIELD_VALUE_OPTIONS = PASS_FIELD_VALUE_OPTIONS

export const PLACEHOLDER_GUEST_NAME = "Guest Name"
export const PLACEHOLDER_GUEST_EMAIL = "Guest Email"
// A ticket's external_id is an 8-character nanoid, not a sequential number.
export const PLACEHOLDER_TICKET_ID = "aB3xK9pQ"

/** Stand-in shown under "Use Account Name" if the account has no name. */
export const LOGO_TEXT_PLACEHOLDER = "Global Summits"

/** GooglePass#brand_name falls back to this. */
export const GOOGLE_DEFAULT_ISSUER_NAME = "Diobox"

/** WalletPass::Customization default branding — shown when the event has none of its own. */
export const DEFAULT_SQUARE_LOGO_URL = "https://data.diobox.com/app/web/assets/wallet-pass/diobox-square-logo-blue.png"
export const DEFAULT_HORIZONTAL_LOGO_URL = "https://data.diobox.com/app/web/assets/wallet-pass/diobox-horizontal-logo-blue.png"
export const DEFAULT_SECONDARY_LABEL = "Location"
export const DEFAULT_GUEST_NAME_LABEL = "Guest"

/** Back-section types that render as tappable link rows in the Google pass
 *  details. Apple back fields are plain text that iOS auto-links, so its
 *  preview colors email/phone/website as links but renders location as text
 *  (iOS links addresses inline rather than as a dedicated row). */
export const GOOGLE_BACK_SECTION_LINK_TYPES = new Set(["email", "phone", "website", "location"])
export const APPLE_BACK_SECTION_LINK_TYPES = new Set(
  [...GOOGLE_BACK_SECTION_LINK_TYPES].filter((type) => type !== "location"),
)

export const defaultWalletPassBuilderValues = (): WalletPassBuilderFormValues => ({
  squareLogo: null,
  squareLogoUrl: "",
  horizontalLogo: null,
  horizontalLogoUrl: "",
  appleBanner: null,
  appleBannerUrl: "",
  googleHero: null,
  googleHeroUrl: "",
  // Matches the API's WalletPass::Customization::DEFAULTS, so the preview of
  // an uncustomized event shows what the generated pass actually looks like.
  backgroundColor: "#EBECF1",
  textColor: "#161616",
  labelColor: "#3A7AD8",
  brandNameMode: "default",
  logoText: "",
  // Blank lets the API fall back to the event's own start time and date.
  headerMode: "default",
  headerLabel: "",
  headerValueSource: "static",
  headerStaticValue: "",
  eventTitleMode: "default",
  primaryLabel: "Event",
  primaryValueSource: "event_name",
  primaryStaticValue: "",
  primaryMovedBelowBanner: false,
  eventLocationMode: "default",
  secondaryLabel: DEFAULT_SECONDARY_LABEL,
  secondaryValueSource: "event_venue",
  secondaryStaticValue: "",
  // Guest Name has its own field now, so the auxiliaries start with what used
  // to sit beside it and one free slot.
  auxiliary1Enabled: true,
  auxiliary1Label: "Ticket",
  auxiliary1ValueSource: "form_name",
  auxiliary1StaticValue: "",
  auxiliary2Enabled: false,
  auxiliary2Label: "",
  auxiliary2ValueSource: "hide",
  auxiliary2StaticValue: "",
  guestNameEnabled: true,
  guestNameLabel: DEFAULT_GUEST_NAME_LABEL,
  qrCodeEnabled: true,
  backSections: [],
})

export type PassFieldEventContext = {
  name?: string
  venue?: string
  address?: string
}

/** Mirrors WalletPass::Customization.resolve. Blank means the API omits the field. */
export const resolveFieldPreviewValue = (
  source: PassFieldValueSource,
  staticValue: string,
  event: PassFieldEventContext = {},
): string => {
  switch (source) {
    case "event_name": return event.name || ""
    case "guest_name": return PLACEHOLDER_GUEST_NAME
    case "guest_email": return PLACEHOLDER_GUEST_EMAIL
    case "ticket_id": return PLACEHOLDER_TICKET_ID
    case "form_name": return "RSVP"
    case "event_venue": return event.venue || event.address || ""
    case "hide": return ""
    default: return staticValue
  }
}

export type PassAuxiliaryField = { label: string; value: string }

/** Resolves the three auxiliary fields to label/value pairs for the preview,
 *  dropping any with no value (hidden or empty) — mirrors the backend, which
 *  omits blank-value fields from `auxiliaryFields`. */
export const resolveAuxiliaryPreviewFields = (
  values: WalletPassBuilderFormValues,
  event: PassFieldEventContext = {},
): PassAuxiliaryField[] => (
  ([
    [values.auxiliary1Enabled, values.auxiliary1Label,
      values.auxiliary1ValueSource, values.auxiliary1StaticValue],
    [values.auxiliary2Enabled, values.auxiliary2Label,
      values.auxiliary2ValueSource, values.auxiliary2StaticValue],
  ] as const)
    .map(([enabled, label, source, staticValue]) => ({
      label,
      value: enabled ? resolveFieldPreviewValue(source, staticValue, event) : "",
    }))
    .filter((field) => field.value !== "")
)
